import { Flex, Text, GridAlpha, Tag } from '@applyboard/crystal-ui';
import { BookOutlineIcon, StarOutlineIcon } from '@applyboard/ui-icons';
import { LabelValue } from '~/components/uie-fix/LabelValue';
import { TranscriptDocument } from '~/types/transcriptExtraction';
import { formatDateToLocal } from '~/utils';
import {
  resolveAttributeValue,
  isExtractionPending,
  isExtractionFailed,
  isExtractionComplete,
  isExtractionNotAttempted,
  formatTranscriptDate,
  isDocumentTypeValidationFailed,
} from '~/utils/transcriptExtraction';

type ManualFields = {
  credentialReceived: string;
  educationLevel: string;
  educationCountry: string;
  startDate: string;
  endDate: string;
};

type TranscriptExtractionProps = {
  document?: TranscriptDocument;
  manualFields?: ManualFields;
  updatedAt?: string;
  updatedByRole?: string;
};

function UpdatedOnTimestamp({ updatedAt, updatedByRole }: Readonly<{ updatedAt?: string; updatedByRole?: string }>) {
  if (!updatedAt) return null;
  const date = new Date(updatedAt);
  if (isNaN(date.getTime())) return null;
  return (
    <Text variant="bodyS" contrast="mid">
      Updated on {formatDateToLocal(date)}
      {updatedByRole ? ` by ${updatedByRole}` : ''}
    </Text>
  );
}

export function TranscriptExtraction({
  document,
  manualFields,
  updatedAt,
  updatedByRole,
}: Readonly<TranscriptExtractionProps>) {
  // Manual-only mode: No extraction document available
  if (!document && manualFields) {
    return (
      <Flex direction="column" gap={4}>
        <UpdatedOnTimestamp updatedAt={updatedAt} updatedByRole={updatedByRole} />
        <ManualFieldsGrid manualFields={manualFields} />
      </Flex>
    );
  }

  if (!document) {
    return null;
  }

  if (isExtractionPending(document)) {
    return (
      <Flex direction="column" gap={6}>
        {manualFields && (
          <>
            <UpdatedOnTimestamp updatedAt={updatedAt} updatedByRole={updatedByRole} />
            <ManualFieldsGrid manualFields={manualFields} />
          </>
        )}
        <Flex direction="column" gap={2} p={4}>
          <Text variant="bodyM" intent="secondary">
            Processing transcript...
          </Text>
        </Flex>
      </Flex>
    );
  }

  if (isExtractionFailed(document) || isDocumentTypeValidationFailed(document)) {
    return (
      <Flex direction="column" gap={6}>
        {manualFields && (
          <>
            <UpdatedOnTimestamp updatedAt={updatedAt} updatedByRole={updatedByRole} />
            <ManualFieldsGrid manualFields={manualFields} />
          </>
        )}
        <Flex direction="column" gap={2} p={4}>
          <Text variant="bodyM" intent="secondary">
            Extraction unavailable – view transcript file.
          </Text>
        </Flex>
      </Flex>
    );
  }

  // Extraction was not attempted (AI processing disabled or unavailable)
  if (isExtractionNotAttempted(document)) {
    return (
      <Flex direction="column" gap={4}>
        {manualFields && (
          <>
            <UpdatedOnTimestamp updatedAt={updatedAt} updatedByRole={updatedByRole} />
            <ManualFieldsGrid manualFields={manualFields} />
          </>
        )}
      </Flex>
    );
  }

  if (isExtractionComplete(document)) {
    // Count courses from courses array
    const extractedCourses = document.attributes.courses;
    const courseCount = extractedCourses?.length ?? 0;

    // Get GPA/grade information from flattened overallTotal
    const overallTotal = document.attributes.overallTotal?.extractedValue;
    const gpa = overallTotal?.gpa ?? overallTotal?.grade ?? overallTotal?.obtained;

    // Determine confidence level from any extracted field
    const confidenceLevel =
      document.attributes.schoolName?.extractedValueConfidence ||
      document.attributes.degree?.extractedValueConfidence ||
      document.attributes.educationLevel?.extractedValueConfidence;

    return (
      <Flex direction="column" gap={4}>
        <UpdatedOnTimestamp updatedAt={updatedAt} updatedByRole={updatedByRole} />

        <Flex gap={6} align="center">
          {courseCount > 0 && (
            <Flex gap={2} align="center">
              <BookOutlineIcon size="md" />
              <Text variant="bodyM">{courseCount} Courses</Text>
            </Flex>
          )}
          {gpa && (
            <Flex gap={2} align="center">
              <StarOutlineIcon size="md" />
              <Text variant="bodyM">Overall GPA: {gpa}</Text>
            </Flex>
          )}
          {confidenceLevel && (
            <Tag icon={confidenceLevel === 'high' ? 'fill' : 'outline'} intent="secondary" size="sm">
              {confidenceLevel === 'high' ? 'High Confidence' : 'Low Confidence'}
            </Tag>
          )}
        </Flex>
        <TranscriptExtractionFields document={document} manualFields={manualFields} />
      </Flex>
    );
  }

  return null;
}

function ManualFieldsGrid({ manualFields }: Readonly<{ manualFields: ManualFields }>) {
  return (
    <GridAlpha rows="repeat(2, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
      <GridAlpha.Item>
        <LabelValue label="EDUCATION LEVEL" value={manualFields.educationLevel} />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <LabelValue label="EDUCATION COUNTRY" value={manualFields.educationCountry} />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <LabelValue label="CREDENTIAL GAINED" value={manualFields.credentialReceived} />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <LabelValue label="EDUCATION START" value={manualFields.startDate} />
      </GridAlpha.Item>
      <GridAlpha.Item>
        <LabelValue label="EDUCATION END (OR EXPECTED)" value={manualFields.endDate} />
      </GridAlpha.Item>
    </GridAlpha>
  );
}

function TranscriptExtractionFields({
  document,
  manualFields,
}: Readonly<{
  document: TranscriptDocument;
  manualFields?: ManualFields;
}>) {
  const { attributes } = document;
  const educationLevel = manualFields?.educationLevel || resolveAttributeValue(attributes.educationLevel);
  const educationCountry = manualFields?.educationCountry || resolveAttributeValue(attributes.issuingCountry);
  const credentialGained =
    resolveAttributeValue(attributes.credentialReceived) ?? manualFields?.credentialReceived ?? '';
  const startDate = resolveAttributeValue(attributes.startDate) || manualFields?.startDate;
  const endDate =
    resolveAttributeValue(attributes.degreeEndDate) ||
    resolveAttributeValue(attributes.endDate) ||
    manualFields?.endDate;

  return (
    <GridAlpha rows="repeat(2, auto)" columnGap={2} rowGap={6} columns="repeat(3, 1fr)">
      {educationLevel && (
        <GridAlpha.Item>
          <LabelValue label="EDUCATION LEVEL" value={educationLevel ?? '--'} />
        </GridAlpha.Item>
      )}
      {educationCountry && (
        <GridAlpha.Item>
          <LabelValue label="EDUCATION COUNTRY" value={educationCountry ?? '--'} />
        </GridAlpha.Item>
      )}

      <GridAlpha.Item>
        <LabelValue label="CREDENTIAL GAINED" value={credentialGained || '--'} />
      </GridAlpha.Item>

      {startDate && (
        <GridAlpha.Item>
          <LabelValue
            label="EDUCATION START"
            value={
              typeof startDate === 'string' && startDate.includes('-') ? formatTranscriptDate(startDate) : startDate
            }
          />
        </GridAlpha.Item>
      )}
      {endDate && (
        <GridAlpha.Item>
          <LabelValue
            label="EDUCATION END (OR EXPECTED)"
            value={typeof endDate === 'string' && endDate.includes('-') ? formatTranscriptDate(endDate) : endDate}
          />
        </GridAlpha.Item>
      )}
    </GridAlpha>
  );
}
